import React, { useState, useEffect, useRef } from "react";
import { getDatabase, ref, push, onChildAdded, get } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import {
  Camera,
  Send,
  X,
  Loader2,
  ArrowLeft,
  MoreVertical,
  Phone,
  Video,
} from "lucide-react";
import { FaBlog } from "react-icons/fa";

export default function SupportChat() {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        loadUserName(currentUser.uid);
        loadMessages(currentUser.uid);
      } else {
        window.location.href = "/prediccion-zone/start";
      }
    });
    

    return () => unsubscribe();
  }, []);

  const loadUserName = async (userId) => {
    try {
      const db = getDatabase();
      const userRef = ref(db, `usuarios/${userId}`);
      const snapshot = await get(userRef);
      if (snapshot.exists()) {
        const userData = snapshot.val();
        setUserName(userData.name || "Usuario");
      }
    } catch (error) {
      console.error("Error loading user name:", error);
    }
  };

  const loadMessages = (userId) => {
    const db = getDatabase();
    const chatRef = ref(db, `chats/${userId}`);
    onChildAdded(chatRef, (snapshot) => {
      const message = { ...snapshot.val(), id: snapshot.key };
      setMessages((prevMessages) => [...prevMessages, message]);
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("La imagen no debe superar los 5MB");
        return;
      }

      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
    const input = document.getElementById("image-upload");
    if (input) input.value = "";
  };

  const uploadImage = async () => {
    if (image) {
      const storage = getStorage();
      const imageRef = storageRef(
        storage,
        `chat-images/${Date.now()}_${image.name}`
      );
      await uploadBytes(imageRef, image);
      return getDownloadURL(imageRef);
    }
    return null;
  };

  const handleCall = () => {
    window.location.href = "tel:+573023580862"; // Usa el número que necesites
  };

  const handleCallVideo = () => {
    window.open("https://wa.me/573023580862", "_blank");
  };
  const handleBlog = () =>{
    window.open("https://deportivosblogs.online/", "_blank");
  }

  const sendMessage = async () => {
    if ((!newMessage.trim() && !image) || isLoading) return;

    setIsLoading(true);
    try {
      const imageUrl = await uploadImage();
      const db = getDatabase();
      const chatRef = ref(db, `chats/${user.uid}`);
      await push(chatRef, {
        text: newMessage.trim(),
        sender: user.uid,
        timestamp: Date.now(),
        userName: userName,
        imageUrl: imageUrl,
      });

      setNewMessage("");
      removeImage();
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Error al enviar el mensaje. Por favor, intente nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Auto scroll mejorado
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    };

    // Pequeño delay para asegurar que el DOM se actualice
    const timer = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timer);
  }, [messages]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "ahora";
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;

    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const isAdmin = (sender, userName) => {
    return userName === "Admin" || sender !== user?.uid;
  };

  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-blue-400 mx-auto mb-4" />
          <p className="text-blue-200">Conectando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header mejorado */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => window.history.back()}
              className="p-1 hover:bg-blue-500 rounded-full transition-colors"
            >
              <ArrowLeft size={20} className="text-white" />
            </button>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">ST</span>
                </div>
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-blue-600"></div>
              </div>
              <div>
                <h2 className="text-white font-semibold">Soporte Técnico</h2>
                <p className="text-blue-100 text-xs">En línea</p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCall}
              className="p-2 hover:bg-blue-500 rounded-full transition-colors"
            >
              <Phone size={18} className="text-white" />
            </button>
            <button
              onClick={handleCallVideo}
              className="p-2 hover:bg-blue-500 rounded-full transition-colors"
            >
              <Video size={18} className="text-white" />
            </button>
            <button
              onClick={handleBlog}
              className="p-2 hover:bg-blue-500 rounded-full transition-colors">
              <FaBlog size={18} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Área de mensajes mejorada */}
      <div
        ref={messageContainerRef}
        className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-thin scrollbar-thumb-blue-600 scrollbar-track-transparent"
        style={{
          backgroundImage: `
            radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
            url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
          `,
        }}
      >
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mb-6 shadow-2xl">
              <span className="text-white font-bold text-2xl">ST</span>
            </div>
            <h3 className="text-white text-xl font-semibold mb-2">
              ¡Hola {userName}!
            </h3>
            <p className="text-blue-200 max-w-md">
              Bienvenido al soporte técnico. Estamos aquí para ayudarte con
              cualquier duda o problema que tengas.
            </p>
          </div>
        )}

        {messages.map((message, index) => {
          const isAdminMessage = isAdmin(message.sender, message.userName);
          const showAvatar =
            index === 0 ||
            isAdmin(
              messages[index - 1].sender,
              messages[index - 1].userName
            ) !== isAdminMessage;

          return (
            <div
              key={message.id || index}
              className={`flex items-end space-x-3 ${
                isAdminMessage ? "justify-start" : "justify-end"
              } animate-in slide-in-from-bottom-2 duration-300`}
            >
              {/* Avatar para admin */}
              {isAdminMessage && (
                <div
                  className={`${
                    showAvatar ? "opacity-100" : "opacity-0"
                  } transition-opacity`}
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-lg mb-1">
                    <span className="text-white font-bold text-xs">ST</span>
                  </div>
                </div>
              )}

              <div
                className={`max-w-[80%] ${!isAdminMessage ? "order-1" : ""}`}
              >
                {/* Nombre del remitente */}
                {showAvatar && (
                  <div
                    className={`text-xs font-medium mb-1 px-1 ${
                      isAdminMessage
                        ? "text-emerald-300"
                        : "text-blue-300 text-right"
                    }`}
                  >
                    {message.userName ||
                      (isAdminMessage ? "Soporte Técnico" : "Tú")}
                  </div>
                )}

                <div
                  className={`relative px-4 py-3 rounded-2xl shadow-xl backdrop-blur-sm border transition-all duration-200 hover:shadow-2xl ${
                    isAdminMessage
                      ? "bg-white/95 text-gray-800 rounded-bl-md border-white/20"
                      : "bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-br-md border-blue-400/30"
                  }`}
                >
                  {/* Texto del mensaje */}
                  {message.text && (
                    <div className="break-words leading-relaxed font-medium">
                      {message.text}
                    </div>
                  )}

                  {/* Imagen del mensaje */}
                  {message.imageUrl && (
                    <div className="mt-3 overflow-hidden rounded-xl">
                      <img
                        src={message.imageUrl}
                        alt="Mensaje"
                        className="max-h-64 w-auto object-contain shadow-lg hover:scale-105 transition-transform duration-200"
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Timestamp y estado */}
                  <div
                    className={`flex items-center justify-end mt-2 space-x-1 ${
                      isAdminMessage ? "text-gray-500" : "text-blue-100"
                    }`}
                  >
                    <span className="text-xs font-medium">
                      {formatTime(message.timestamp)}
                    </span>
                    {!isAdminMessage && (
                      <div className="flex space-x-1">
                        <div className="text-xs">✓</div>
                        {message.read && (
                          <div className="text-xs text-green-300">✓</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* Input mejorado */}
      <div className="bg-slate-800/80 backdrop-blur-lg p-4 border-t border-slate-700/50">
        {imagePreview && (
          <div className="mb-4 flex items-center gap-3">
            <div className="relative h-20 w-20">
              <img
                src={imagePreview}
                alt="Preview"
                className="h-full w-full rounded-xl object-cover shadow-lg border-2 border-blue-500/30"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute -right-2 -top-2 rounded-full bg-gradient-to-r from-red-500 to-red-600 p-1.5 text-white hover:from-red-600 hover:to-red-700 shadow-lg transition-all duration-200 hover:scale-110"
              >
                <X size={12} />
              </button>
            </div>
            <div className="text-blue-200 text-sm">
              <p className="font-medium">Imagen seleccionada</p>
              <p className="text-blue-300">Lista para enviar</p>
            </div>
          </div>
        )}

        <div className="flex items-end gap-3">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
            id="image-upload"
          />

          <label
            htmlFor="image-upload"
            className="cursor-pointer p-3 hover:bg-slate-700 rounded-xl transition-all duration-200 hover:scale-105 bg-slate-700/50"
          >
            <Camera size={20} className="text-blue-400" />
          </label>

          <div className="flex-1 bg-slate-700/80 backdrop-blur-sm rounded-2xl px-5 py-3 border border-slate-600/50 focus-within:ring-2 focus-within:ring-blue-500/50 focus-within:border-blue-500/50 transition-all duration-200">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe tu mensaje..."
              className="w-full bg-transparent text-white placeholder-slate-400 focus:outline-none font-medium"
              disabled={isLoading}
            />
          </div>

          <button
            onClick={sendMessage}
            disabled={isLoading || (!newMessage.trim() && !image)}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed p-3 rounded-xl text-white transition-all duration-200 transform hover:scale-105 disabled:hover:scale-100 shadow-lg hover:shadow-xl"
          >
            {isLoading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
